import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
} from '@heroui/react';
import { Download } from 'lucide-react';
import React, { FC } from 'react';

import { useGetStockUsage } from '../../../../queries';
import { WatermarkedPreview } from '../../utils/scene';

interface LicenseAssetsModalProps {
  previews: WatermarkedPreview[];
  isLicensing: boolean;
  progress: number;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Okno przed pobraniem kreacji, w której siedzą jeszcze robocze podglądy.
 *
 * Nie blokujemy pracy z podglądem, bo do układania kreacji jest wygodny.
 * Blokujemy dopiero eksport i proponujemy jedno kliknięcie, które dociąga
 * licencjonowane pliki i podmienia je w miejscu.
 */
export const LicenseAssetsModal: FC<LicenseAssetsModalProps> = ({
  previews,
  isLicensing,
  progress,
  onConfirm,
  onClose,
}) => {
  const { data: usage } = useGetStockUsage();
  const left = usage?.left ?? 0;
  const notEnough = usage !== undefined && left < previews.length;

  return (
    <Modal isOpen size="md" radius="sm" onClose={isLicensing ? undefined : onClose}>
      <ModalContent>
        <ModalHeader>Najpierw pobierz zdjęcia z licencją</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-700 leading-relaxed">
            {`Na planszy ${previews.length === 1 ? 'jest ' : 'są '}`}
            <strong>{`${previews.length} ${previews.length === 1 ? 'zdjęcie' : 'zdjęcia'} ze znakiem wodnym`}</strong>
            {'. Wersji roboczej nie da się wyeksportować, bo znak wodny zostałby w pliku, '}
            a przeglądarka i tak blokuje zapis takiego płótna.
          </p>

          <div className="flex flex-wrap gap-2">
            {previews.map((preview) => (
              <img
                key={preview.src}
                src={preview.src}
                alt=""
                className="h-16 w-16 rounded border border-gray-200 object-cover"
              />
            ))}
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            {`Pobranie zabierze ${previews.length} z dziennej puli. Zostało jej dzisiaj ${left}. `}
            Układ, kadr i filtry zostają bez zmian, podmieniamy sam plik.
          </p>

          {notEnough && (
            <p className="text-xs text-danger leading-relaxed">
              Dzienna pula nie wystarczy na wszystkie zdjęcia. Usuń część podglądów albo spróbuj jutro.
            </p>
          )}

          {isLicensing && (
            <Progress
              size="sm"
              radius="sm"
              aria-label="Postęp pobierania"
              label={`Pobieram: ${progress} z ${previews.length}`}
              value={progress}
              maxValue={previews.length}
              classNames={{ label: 'text-xs text-gray-500' }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" radius="sm" variant="light" isDisabled={isLicensing} onPress={onClose}>
            Anuluj
          </Button>
          <Button
            size="sm"
            radius="sm"
            className="bg-black text-white"
            startContent={<Download size={16} />}
            isLoading={isLicensing}
            isDisabled={notEnough}
            onPress={onConfirm}
          >
            Pobierz z licencją i zapisz plik
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
